import './OrganisationPicker.scoped.scss'
import Node from './Node'
import Loading from 'Shared/components/Loading/Loading'
import restClient from 'Shared/hooks/restClient'

const TREE_URL = '/api/organisations/tree'
const SEARCH_URL = '/api/organisations/search'

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

function externalTypeLabel(type) {
  const kinds = window.ENV?.ORGANISATION_KINDS || []
  const kind = kinds.find((k) => k.external_type === type)
  return kind ? kind.label : capitalize(type)
}

function getRootOrgs() {
  return restClient.get(TREE_URL).then((r) => r.data)
}

function getChildren(orgId, externalType) {
  return restClient.get(`${TREE_URL}/${orgId}?external_type=${externalType || ''}`).then((r) => r.data)
}

function formatSearchResult(org) {
  return {
    id: org.value,
    parentNames: org.parentNames,
    name: org.label.replace(/\s+\(.*\)$/, ''),
    externalType: org.externalType,
    externalId: org.externalId,
    hasChildren: false,
  }
}

function searchOrgs(query) {
  return restClient.get(`${SEARCH_URL}?query=${encodeURIComponent(query)}`)
    .then((r) => r.data.map(formatSearchResult))
}


function OrgNode({
  name, id, depth = 0, onClick, hasChildOrgs = false,
  externalTypes,  // loaded along with root node
  externalType, // external type if this is a child node
  isExternalType = false, // is this a fake org representing an external type
  parentNames, // for search results only
  externalId, // for search results only
  organisationTypesClickable = false,
  ...props }
) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [childOrgs, setChildOrgs] = useState(null)
  const [hasMore, setHasMore] = useState(false)


  const toggleNode = () => {
    setExpanded(!expanded)

    // If passed externalTypes which are loaded along with the root node
    if (externalTypes) {
      // Create fake orgs to represent the external types rather than loading real child orgs
      setChildOrgs(externalTypes.map((type) => (
        {
          id: `${id}-${type}`,
          name: externalTypeLabel(type),
          hasChildren: true,
          isExternalType: true,

        }
      )))
      return
    }

    setLoading(true)
    if (childOrgs) return

    // External type node acts as a root node but includes the type in the id for use when filtering children
    let type = null;
    if (isExternalType) {
      type = id.split('-')[1]
    }

    getChildren(id, type).then((data) => {
      setLoading(false)
      setChildOrgs(data.organisations)
      setHasMore(data.hasMore)
    })
  }

  // Immediately load first level of children
  useEffect(() => {
    if (depth === 0 && hasChildOrgs) {
      toggleNode()
    }
  }, [])

  const onNodeClick = (id, name) => {
    if (organisationTypesClickable && typeof(organisationTypesClickable) === 'string') {
      if (externalType !== organisationTypesClickable) {
        toggleNode()
        return
      }
    }
    if (!organisationTypesClickable && isExternalType) {
      toggleNode()
      return
    }
    onClick(id, name)
  }

  return (
    <Node
      name={name}
      parentNames={parentNames}
      externalId={externalId}
      depth={depth}
      expandable={depth > 0 && hasChildOrgs}
      expanded={expanded}
      label={externalType ? externalTypeLabel(externalType) : undefined}
      toggle={toggleNode}
      onClick={() => onNodeClick(id, name)}
    >

      {childOrgs && expanded &&
        <div className="org-children">
          {childOrgs.map(({ name, id, hasChildren, externalType, isExternalType }) => (
            <OrgNode
              depth={depth + 1}
              name={name} id={id}
              onClick={onClick}
              hasChildOrgs={hasChildren}
              isExternalType={isExternalType}
              externalType={externalType}
              key={id}
              organisationTypesClickable={organisationTypesClickable} />
          ))}
          {hasMore &&
            <Node
              name="More organisations exist but were not loaded"
              depth={depth + 1}
              expandable={false}
              textOnly={true}
            />
          }
        </div>
      }

    </Node>
  )
}


export default function OrganisationPicker({ onPick, className = '', organisationTypesClickable = false }) {
  const [loading, setLoading] = useState(true)
  const [rootOrgs, setRootOrgs] = useState(null)
  const [externalTypes, setExternalTypes] = useState(null)
  const [searchResultOrgs, setSearchResultOrgs] = React.useState(null)
  const searchInputRef = React.useRef(null)

  let displayOrgs = null
  if (rootOrgs && !searchResultOrgs) {
    displayOrgs = rootOrgs
  } else if (searchResultOrgs && searchResultOrgs.length > 0) {
    displayOrgs = searchResultOrgs
  }

  useEffect(() => {
    getRootOrgs().then((data) => {
      setRootOrgs(data.organisations)
      setExternalTypes(data.externalTypes || [])
      setLoading(false)
    })
  }, [])

  const search = (value) => {
    if (value === '') {
      setSearchResultOrgs(null)
      return
    }
    setLoading(true)
    searchOrgs(value).then((orgs) => {
      setLoading(false)
      setSearchResultOrgs(orgs)
    })
  }
  const debouncedSearch = _.debounce(search, 500)

  return (
    <div className={'organisation-picker ' + className} >
      <div>
        <input type="search" ref={searchInputRef} placeholder="Search&hellip;" className="full-width" onChange={(e) => debouncedSearch(e.target.value)} />
      </div>

      <div className="orgs">
        {loading && <Loading />}

        {!loading && searchResultOrgs && searchResultOrgs.length === 0 && <div>No matching organisations found</div>}

        {!loading && displayOrgs && displayOrgs.map(({ name, id, hasChildren, parentNames, externalType, externalId }) => (
          <OrgNode
            name={name}
            id={id}
            onClick={onPick}
            hasChildOrgs={hasChildren}
            key={id}
            externalTypes={externalTypes}
            externalType={externalType}
            externalId={externalId}
            parentNames={parentNames}
            organisationTypesClickable={organisationTypesClickable}
          />
        ))}
      </div>
    </div>
  )
}

